import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import { billingApi } from '../../services/billing';
import type { BillingOverview, BillingUsage } from '../../types';

function getStatusBadge(status: string | null) {
  switch (status) {
    case 'active':
    case 'trialing':
      return (
        <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
          {status === 'trialing' ? 'Trialing' : 'Active'}
        </span>
      );
    case 'past_due':
      return (
        <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
          Past Due
        </span>
      );
    case 'canceled':
      return (
        <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
          Canceled
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500">
          No Subscription
        </span>
      );
  }
}

function getProgressColor(used: number, limit: number): string {
  if (limit >= 9999) return 'bg-emerald-500';
  const pct = (used / limit) * 100;
  if (pct > 90) return 'bg-red-500';
  if (pct >= 75) return 'bg-amber-500';
  return 'bg-emerald-500';
}

function formatLimit(value: number): string {
  return value >= 9999 ? 'Unlimited' : String(value);
}

function UsageMeter({ label, used, limit }: { label: string; used: number; limit: number }) {
  const isUnlimited = limit >= 9999;
  const pct = isUnlimited ? Math.min((used / 100) * 100, 100) : Math.min((used / limit) * 100, 100);
  const barColor = getProgressColor(used, limit);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
      <p className="text-sm font-medium text-slate-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-slate-900">
        {used} <span className="text-sm font-normal text-slate-400">/ {formatLimit(limit)}</span>
      </p>
      <div className="mt-3 h-2 w-full rounded-full bg-slate-100">
        <div
          className={`h-2 rounded-full transition-all ${barColor}`}
          style={{ width: `${isUnlimited ? Math.min(used, 100) : pct}%` }}
        />
      </div>
    </div>
  );
}

export default function VendorBilling() {
  const [portalLoading, setPortalLoading] = useState(false);

  const { data: overview, isLoading } = useQuery({
    queryKey: ['billing', 'overview'],
    queryFn: () => billingApi.getOverview().then((r) => r.data),
  });

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const res = await billingApi.createPortalSession();
      window.location.href = res.data.portal_url;
    } catch {
      setPortalLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const plan = overview?.current_plan ?? null;
  const status = overview?.subscription_status ?? null;
  const periodEnd = overview?.subscription_period_end ?? null;
  const usage: BillingUsage = overview?.usage ?? {
    guards_used: 0,
    guards_limit: 0,
    sites_used: 0,
    sites_limit: 0,
    uploads_used: 0,
    uploads_limit: 0,
  };

  return (
    <>
      <PageHeader
        title="Billing & Plans"
        description="Manage your subscription and billing"
      />

      {/* Current Plan Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-xl font-bold text-slate-900">
                {plan ? plan.name : 'No Plan'}
              </h2>
              {getStatusBadge(status)}
            </div>
            {plan && (
              <p className="text-3xl font-bold text-slate-900 mt-2">
                ${plan.price_monthly}
                <span className="text-sm font-normal text-slate-400">/mo</span>
              </p>
            )}
            {periodEnd && (
              <p className="text-sm text-slate-500 mt-2">
                Next billing:{' '}
                {new Date(periodEnd).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/vendor/billing/plans"
              className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:from-blue-700 hover:to-blue-800 transition-all"
            >
              Change Plan
            </Link>
            <button
              onClick={handleManageSubscription}
              disabled={portalLoading}
              className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              {portalLoading ? 'Redirecting...' : 'Manage Subscription'}
            </button>
          </div>
        </div>
      </div>

      {/* Usage Meters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
        <UsageMeter label="Guards" used={usage.guards_used} limit={usage.guards_limit} />
        <UsageMeter label="Sites" used={usage.sites_used} limit={usage.sites_limit} />
        <UsageMeter label="Uploads This Month" used={usage.uploads_used} limit={usage.uploads_limit} />
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Link
          to="/vendor/billing/invoices"
          className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 hover:border-blue-200 hover:shadow-md transition-all group"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                View Invoices
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                View and download past invoices
              </p>
            </div>
            <svg
              className="h-5 w-5 text-slate-400 group-hover:text-blue-600 transition-colors"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </div>
        </Link>
        <Link
          to="/vendor/billing/payment"
          className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 hover:border-blue-200 hover:shadow-md transition-all group"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                Payment Methods
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                Manage your cards and payment options
              </p>
            </div>
            <svg
              className="h-5 w-5 text-slate-400 group-hover:text-blue-600 transition-colors"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </div>
        </Link>
      </div>
    </>
  );
}
